import { type ComponentPropsWithoutRef, forwardRef, memo, type ReactNode } from 'react';

import { Command } from '@air/cmdk';
import { SearchIcon } from '@air/design-icons';
import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { searchFieldText } from '~/components/Search/searchFieldChrome';
import {
  SearchCommandFilterCount,
  type SearchCommandFilterCountProps,
} from '~/components/SearchCommand/SearchCommandFilterCount';
import { SearchCommandInputPlaceholder } from '~/components/SearchCommand/SearchCommandInputPlaceholder';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type SearchCommandInputProps = Pick<
  ComponentPropsWithoutRef<typeof Command.Input>,
  'className' | 'defaultValue' | 'onValueChange' | 'placeholder' | 'value'
> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    children?: ReactNode;
    compact?: boolean;
  };

export const SearchCommandInput = memo(
  forwardRef<HTMLInputElement, SearchCommandInputProps>(
    (
      { children, className, compact, count, defaultValue, onValueChange, placeholder, value }: SearchCommandInputProps,
      ref,
    ) => {
      const { ui2026 } = useTemporaryFeatureFlags();
      const { isAboveMediumScreen } = useBreakpointsContext();
      const useDesktopUi2026Input = ui2026 && isAboveMediumScreen;

      if (!useDesktopUi2026Input) {
        return (
          <div
            className={cn(
              'relative -m-px flex shrink-0 gap-1 overflow-hidden rounded-t-4 border border-transparent ads:bg-base bg-gray-1',
              compact ? 'h-8' : 'h-10',
              className,
            )}
            data-testid="SEARCH_COMMAND_INPUT"
          >
            <SearchIcon
              className="absolute top-1/2 left-3 -translate-y-1/2 ads:text-icon-subtle text-gray-9"
              size="sm"
            />
            <Command.Input
              className={cn(
                'size-full rounded-8 bg-transparent pl-9 font-medium ads:text-primary text-gray-12 outline-none',
                compact
                  ? 'pr-10 text-14 placeholder:text-transparent'
                  : 'text-16 ads:placeholder:text-placeholder placeholder:text-gray-9',
              )}
              data-testid="SEARCH_COMMAND_INPUT_INPUT"
              defaultValue={defaultValue}
              onValueChange={onValueChange}
              placeholder={placeholder}
              ref={ref}
              value={value}
            />
            {compact && !value && placeholder && (
              <SearchCommandInputPlaceholder className="left-9" placeholder={placeholder} />
            )}
            {!!count && <SearchCommandFilterCount count={count} />}
          </div>
        );
      }

      return (
        <div
          className={cn(
            'relative flex h-full min-h-0 w-full shrink-0 items-stretch gap-1 overflow-hidden border border-transparent ads:bg-base bg-gray-1 px-1',
            className,
          )}
          data-testid="SEARCH_COMMAND_INPUT"
        >
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center self-center"
            data-testid="SEARCH_COMMAND_LEADING_SLOT"
          >
            <SearchIcon className="shrink-0 ads:text-icon-default text-gray-10" size="sm" />
          </span>
          <Command.Input
            className={cn(
              searchFieldText(),
              'h-full min-w-0 flex-1 self-stretch rounded-8 bg-transparent py-1 pl-0',
              compact ? 'pr-10 text-14 placeholder:text-transparent' : undefined,
            )}
            data-testid="SEARCH_COMMAND_INPUT_INPUT"
            defaultValue={defaultValue}
            onValueChange={onValueChange}
            placeholder={placeholder}
            ref={ref}
            value={value}
          />
          {compact && !value && placeholder && (
            <SearchCommandInputPlaceholder className="left-9" placeholder={placeholder} />
          )}
          {!!count && <SearchCommandFilterCount count={count} />}
          {children ? <span className="flex shrink-0 items-center gap-1">{children}</span> : null}
        </div>
      );
    },
  ),
);

SearchCommandInput.displayName = 'SearchCommandInput';
